import { useState } from "react";

import {
  ampliCurveState,
  ampliCurveStateLabel,
  ampliInconsistentReasonLabel,
} from "../financialProjection/cashFlowPlanning";
import { regenerateAmpliDisbursement } from "./disbursementApi";
import { normalizeDisbursementCoverage } from "./coverage";
import ProjectedCoverage from "./ProjectedCoverage";

const STATE_HINT = Object.freeze({
  ready: "As regras cadastradas já estão refletidas na Projeção Financeira.",
  stale: "A curva anterior continua no ar enquanto a nova é gerada.",
  inconsistent: null, // vem do motivo
  unavailable: "Esta obra ainda não tem previsto canônico para gerar a curva.",
});

const generatedAtLabel = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : `gerado em ${date.toLocaleDateString(
        "pt-BR",
      )} às ${date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
};

const DisbursementHeader = ({
  project,
  readOnly = false,
  onRegenerated,
  moduleState,
}) => {
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(false);
  const displayProject =
    moduleState === undefined
      ? project
      : {
          ...project,
          ampliDisbursement: moduleState
            ? { ...moduleState, state: moduleState.status }
            : null,
        };
  const curve = ampliCurveState(displayProject);
  const coverage = normalizeDisbursementCoverage(
    displayProject?.ampliDisbursement?.coverage,
  );
  const generatedAt = generatedAtLabel(curve.generatedAt);
  const hint = curve.reason
    ? ampliInconsistentReasonLabel(curve.reason)
    : STATE_HINT[curve.status];

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    setError(false);
    try {
      const result = await regenerateAmpliDisbursement(project?._id);
      if (!result || (result.code !== 200 && result.code !== 202)) {
        setError(true);
        return;
      }
      await onRegenerated?.(result);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <section
      className="ampli-disbursement-header"
      aria-label="Situação do desembolso Ampli"
    >
      <hgroup className="dashboard-title">
        <h2>Desembolso</h2>
        <p>
          Cadastre a forma de pagar por família de insumo, insumo ou nível da
          EAP. A plataforma redistribui o previsto no tempo e corrige a curva
          sozinha quando o previsto muda ou o item é contratado.
        </p>
      </hgroup>

      <div className="ampli-disbursement-header__state">
        <span
          className={`ampli-disbursement-badge ampli-disbursement-badge--${curve.status}`}
        >
          {ampliCurveStateLabel(curve.status)}
        </span>
        {generatedAt ? (
          <span className="ampli-disbursement-header__timestamp">
            {generatedAt}
          </span>
        ) : null}
        <p className="ampli-disbursement-header__hint">{hint}</p>
      </div>

      {/* D24: a cobertura é o desembolso projetado pela Ampli; o mesmo bloco aparece no
          cartão da Projeção Financeira. */}
      {coverage ? <ProjectedCoverage coverage={coverage} /> : null}

      {/* O resumo só informa; criar regra/grupo e organizar com IA moram no bloco das
          regras já criadas. A única ação daqui é sobre a própria curva. */}
      {!readOnly &&
      (curve.status === "stale" || curve.status === "inconsistent") ? (
        <div className="ampli-disbursement-header__actions">
          <button
            type="button"
            className="button button-outline small"
            disabled={regenerating}
            onClick={regenerate}
          >
            {regenerating ? "Gerando…" : "Gerar de novo"}
          </button>
        </div>
      ) : null}

      {error ? (
        <p className="alert alert-error" role="alert">
          Não foi possível gerar a curva agora. Tente novamente.
        </p>
      ) : null}
    </section>
  );
};

export default DisbursementHeader;
